import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trophy, Loader2, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";

const RULES = [
  { key: "len", label: "At least 10 characters", test: (p) => p.length >= 10 },
  { key: "lower", label: "A lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "upper", label: "An uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "digit", label: "A number", test: (p) => /[0-9]/.test(p) },
  { key: "symbol", label: "A symbol (! @ # $ % ^ & *)", test: (p) => /[!@#$%^&*\-_?]/.test(p) },
];

export default function Onboarding() {
  const { user, completeOnboarding, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const passOk = useMemo(() => RULES.every((r) => r.test(password)), [password]);
  const nameOk = name.trim().length >= 2;
  const matchOk = confirm.length > 0 && confirm === password;
  const canSubmit = nameOk && passOk && matchOk && !loading;

  if (user === false) return <Navigate to="/login" replace />;
  if (user && !user.needs_onboarding) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setErr("");
    setLoading(true);
    try {
      const u = await completeOnboarding(name.trim(), password);
      toast.success(`Welcome to the arena, ${u.name}`);
      navigate("/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-6">
      <div className="w-full max-w-md">
        <div className="inline-flex items-center gap-2 mb-8" data-testid="onboarding-brand">
          <div className="w-8 h-8 bg-[#178AE5] flex items-center justify-center rounded-sm">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          <span className="font-logo text-xl whitespace-nowrap">SALES<span className="text-[#178AE5]">ARENA HQ</span></span>
        </div>
        <h1 className="font-display text-4xl mb-2">SET UP YOUR PROFILE.</h1>
        <p className="text-[#A1A1AA] mb-8">
          Welcome to {user?.company_name || "the arena"}. Add your name and choose your own password to get started.
        </p>

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <Label htmlFor="ob-name" className="font-label text-[#A1A1AA]">Your name</Label>
            <Input
              id="ob-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Rep"
              className="mt-2 bg-[#121212] border-[#27272A] h-12 rounded-sm focus-visible:ring-[#178AE5]"
              data-testid="onboarding-name-input"
            />
          </div>
          <div>
            <Label htmlFor="ob-pass" className="font-label text-[#A1A1AA]">New password</Label>
            <Input
              id="ob-pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choose a strong password"
              className="mt-2 bg-[#121212] border-[#27272A] h-12 rounded-sm focus-visible:ring-[#178AE5]"
              data-testid="onboarding-password-input"
            />
          </div>

          <div className="space-y-1.5" data-testid="onboarding-rules">
            {RULES.map((r) => {
              const ok = r.test(password);
              return (
                <div key={r.key} className="flex items-center gap-2 text-sm">
                  {ok ? <CheckCircle2 className="w-4 h-4 text-[#34C759]" /> : <Circle className="w-4 h-4 text-[#52525B]" />}
                  <span className={ok ? "text-[#34C759]" : "text-[#71717A]"}>{r.label}</span>
                </div>
              );
            })}
          </div>

          <div>
            <Label htmlFor="ob-confirm" className="font-label text-[#A1A1AA]">Confirm password</Label>
            <Input
              id="ob-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter your password"
              className="mt-2 bg-[#121212] border-[#27272A] h-12 rounded-sm focus-visible:ring-[#178AE5]"
              data-testid="onboarding-confirm-input"
            />
            {confirm.length > 0 && !matchOk && (
              <p className="text-xs text-[#E13C32] mt-1.5">Passwords don&apos;t match yet.</p>
            )}
          </div>

          {err && (
            <div className="text-sm text-[#E13C32] bg-[#E13C32]/10 border border-[#E13C32]/30 p-3 rounded-sm" data-testid="onboarding-error">
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={!canSubmit}
            className="w-full bg-[#178AE5] hover:bg-[#41A0E8] text-white font-label h-12 rounded-sm disabled:opacity-50"
            data-testid="onboarding-submit-button"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter the arena →"}
          </Button>
        </form>

        <button
          type="button"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="mt-6 text-sm text-[#71717A] hover:text-[#A1A1AA] underline"
          data-testid="onboarding-logout"
        >
          Not you? Log out
        </button>
      </div>
    </div>
  );
}
